import { IData, VODay, VOExpedition, VOSensor } from "./DataStructure.js";
import { DateUtil } from "./DateUtil.js";
import { Geom } from "./Geom.js";
import { Label } from "./Label.js";

export class DataManager {
    public onComplete = e5.Signal.create();
    public onProgress = e5.Signal.create<[number]>();

    public data: IData = null;

    private _days: VODay[] = [];
    private _daysLookup: { [key: string]: VODay } = {};
    private _sensorsLookup: { [key: string]: VOSensor[] } = {};
    private _sensorsSort: VOSensor[] = [];

    constructor() {

    }

    public async load(url: string): Promise<boolean> {
        var data = await e5.load("json", url, {
            onProgress: (e) => {
                this.onProgress.dispatch(e.loaded / e.total);
            }
        });

        if (data && data.kind === "error") {
            throw new Error("[DataManager] [state=loaded] " + data.message);
        }
        if (!data) {
            throw new Error("[DataManager] [state=loaded] Empty result");
        }
        await this.finalize(data);

        return true;
    }

    private async finalize(data: IData) {
        this.data = data;

        var hasPos = false;
        for (var sensor of data.sensor) {

            // sensor.latitude = null;
            // sensor.longitude = null;

            if (sensor.latitude !== null
                && sensor.longitude !== null) {
                hasPos = true;
                break;
            }
        }

        data.date_begin = DateUtil.fromDateTimeString(data.date_begin as any);
        data.date_end = DateUtil.fromDateTimeString(data.date_end as any);

        // if (!hasPos) {
        //     var d = new Date();
        //     data.sensor.unshift({
        //         date: "2019-01-15 19:59:20" as any,
        //         distance: null,
        //         id: 0,
        //         latitude: 53.5668,
        //         longitude: 8.55504,
        //         temperature: null,
        //         time: d.getTime(),
        //         wind: null
        //     });
        // }

        var distance = 0;
        var prev: VOSensor = null;
        for (var sensor of data.sensor) {



            sensor.date = DateUtil.fromDateTimeString(sensor.date as any);
            sensor.time = sensor.date.getTime();

            var key = DateUtil.toKey(sensor.date);
            if (!this._sensorsLookup[key]) {
                this._sensorsLookup[key] = [];
            }
            this._sensorsLookup[key].push(sensor);

            if (prev
                && sensor.latitude !== null
                && sensor.longitude !== null) {
                distance += Geom.getDistanceInKm(prev.latitude, prev.longitude, sensor.latitude, sensor.longitude);
                prev = sensor;
            } else if (sensor.latitude !== null
                && sensor.longitude !== null) {
                prev = sensor;
            }
            sensor.distance = distance;
        }
        this._sensorsSort = data.sensor.slice();

        for (var expedition of data.awi_expedition) {
            expedition.date_begin = DateUtil.fromDateTimeString(expedition.date_begin as any);
            expedition.date_end = DateUtil.fromDateTimeString(expedition.date_end as any);
        }
        if(data.expedition){
            data.expedition = data.awi_expedition.id(data.expedition as any);
        }
        

        for (var markerList of data.awi_marker) {
            if (!markerList.markers) {
                markerList.markers = [];
            }

            if (!markerList.show_in_expeditions) {
                markerList.show_in_expeditions = [];
            } else {
                var expeditions: VOExpedition[] = [];
                for (var expeditionId of markerList.show_in_expeditions) {
                    var exp = data.awi_expedition.id(expeditionId as any);
                    if (exp) {
                        expeditions.push(exp);
                    }
                }
                markerList.show_in_expeditions = expeditions;
            }

            if (markerList.visibility === "hidden") {
                markerList.visible = false;
            } else if (markerList.visibility === "always") {
                markerList.visible = true;
            } else if (markerList.visibility === "expedition") {
                markerList.visible = false;
                if (data.expedition
                    && markerList.show_in_expeditions.indexOf(data.expedition) !== -1) {
                    markerList.visible = true;
                }
            }

            for (var marker of markerList.markers) {
                var vals = marker.position.trim().split(",");
                if (vals.length != 2) {
                    console.warn("Could not parse marker position", markerList, marker);
                    marker.visible = false;
                } else {
                    var lat = parseFloat(vals[0]);
                    var lng = parseFloat(vals[1]);
                    if (isNaN(lat)
                        || isNaN(lng)) {
                        console.warn("Error parsing position", markerList, marker);
                        marker.visible = false;
                    } else {
                        marker.lat = lat;
                        marker.lng = lng;
                    }
                }
            }
        }

        var mediaId: number = 1;
        for (var blog of data.awi_blog) {
            blog.date = DateUtil.fromString(blog.date as any);
            blog.expedition = data.awi_expedition.id(blog.expedition as any);
            blog.days = 0;
            if (blog.expedition) {
                blog.days = DateUtil.dayDiff(blog.date, blog.expedition.date_begin) - 1;
            }

            if (blog.medias) {
                for (var m of blog.medias) {

                    m.uid = mediaId++;

                    var title_de = (blog.title_de || blog.title_en);
                    var title_en = (blog.title_en || blog.title_de);

                    var subtitle_de = (m.info.title_de || m.info.title_en);
                    var subtitle_en = (m.info.title_en || m.info.title_de);

                    var de: string[] = [];
                    if (title_de) {
                        de.push(title_de);
                    }
                    if (subtitle_de) {
                        de.push(subtitle_de);
                    }
                    var en: string[] = [];
                    if (title_en) {
                        en.push(title_en);
                    }
                    if (subtitle_de) {
                        en.push(subtitle_en);
                    }

                    m.info.context_title_de = de.join(" | ");
                    m.info.context_title_en = en.join(" | ");

                    if (m.info.author_de) {
                        // m.info.author_de = "© " + m.info.author_de;
                        m.info.author_de = `<i class="fal fa-copyright"></i> ` + e5.Text.escapeHtml(m.info.author_de);
                    }
                    if (m.info.author_en) {
                        // m.info.author_en = "© " + m.info.author_en;
                        m.info.author_en = `<i class="fal fa-copyright"></i> ` + e5.Text.escapeHtml(m.info.author_en);
                    }
                }

                for (var m of blog.medias) {

                    if (
                        !m.info.language
                        || m.info.language === "all"
                        || m.info.language === "de") {
                        blog.media_de = m;
                        break;
                    }
                }
                for (var m of blog.medias) {
                    if (
                        !m.info.language
                        || m.info.language === "all"
                        || m.info.language === "en") {
                        blog.media_en = m;
                        break;
                    }
                }
            }
        }

        /**
         * Create a VODay object for each day of the expedition.
         */
        var daysPerExpedition = DateUtil.dayDiff(data.date_end, data.date_begin) + 1;
        var start = DateUtil.round(data.date_begin);
        var days: VODay[] = [];
        for (var i = 0; i < daysPerExpedition; ++i) {
            var d = DateUtil.round(start);
            var key = DateUtil.toKey(d);
            var day: VODay = {
                blogs: [],
                date: d,
                sensors: this._sensorsLookup[key] || [],
                position: this.getNearestPosition(d),
                sensor: null
            };
            day.sensor = this.getNearestOfDay(day);
            days.push(day);
            this._daysLookup[key] = day;

            start.setUTCDate(start.getUTCDate() + 1);
        }

        for (var blog of data.awi_blog) {
            blog.day = this.getDay(blog.date);
            if (blog.day) {
                blog.day.blogs.push(blog);
            } else {
                console.warn("Could not find VODay for blog.", blog);
            }
        }

        data.awi_blog.sort((a, b) => b.date.getTime() - a.date.getTime());

        return true;
    }

    public getDay(date: Date): VODay {
        date = DateUtil.clamp(date, this.data.date_begin, this.data.date_end);
        return this._daysLookup[DateUtil.toKey(date)];
    }

    private getNearestOfDay(day: VODay): VOSensor {

        var date = day.date;
        date = DateUtil.clamp(date, this.data.date_begin, this.data.date_end);
        date = DateUtil.round(date);

        var now = new Date();
        date = new Date(date);
        // date.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
        date.setUTCHours(now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());

        var time = date.getTime();
        var sensors = day.sensors.slice();
        sensors.sort((a, b) => {
            return Math.abs(a.time - time) - Math.abs(b.time - time);
        });

        var nearest: VOSensor = sensors[0];
        if (!nearest) {
            return null;
        }

        return nearest;
    }

    private getNearestPosition(date: Date): VOSensor {
        date = DateUtil.clamp(date, this.data.date_begin, this.data.date_end);
        date = DateUtil.round(date);

        var now = new Date();
        date = new Date(date);
        // date.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
        date.setUTCHours(now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());

        var time = date.getTime();
        this._sensorsSort.sort((a, b) => {
            return Math.abs(a.time - time) - Math.abs(b.time - time);
        });

        for (var nearest of this._sensorsSort) {
            if (nearest.latitude !== null
                && nearest.longitude !== null) {
                return nearest;
            }
        }

        return null;
    }
}